import { c as _c } from "react/compiler-runtime";
import type { IdentifyRequirementLogin } from '@onefootprint/request-types';
import { useState } from 'react';
import { type Context, IdentifyVariant } from '../../identify.types';
import Challenge from '../challenge';
import DifferentAccountOption from '../different-account-option';
import ChallengeSelectOrPasskey from './components/challenge-select-or-passkey';
import LoginWithUnverifiedEmail from './components/login-with-unverified-email';
import TryAnotherWay from './components/try-another-way';
import { getAvailableMethods } from './components/utils';
export type LoginChallengeProps = {
  context: Omit<Context, 'identifyToken'>;
  requirement: IdentifyRequirementLogin;
  onDone: (delayMs?: number) => Promise<void>;
  onReset?: () => void;
  onPrev?: () => void;
  children?: React.ReactNode;
};
const isUpdateLoginFlow = (v: unknown): v is IdentifyVariant.updateLoginMethods => v === IdentifyVariant.updateLoginMethods;
enum Screen {
  Challenge = 'challenge',
  LoginWithUnverifiedEmail = 'login-with-unverified-email',
}
const LoginChallenge = t0 => {
  const $ = _c(44);
  const {
    context,
    requirement,
    onDone,
    onPrev,
    onReset,
    children
  } = t0;
  let t1;
  if ($[0] !== context.initArgs.device || $[1] !== requirement.user) {
    t1 = getAvailableMethods(requirement.user, context.initArgs.device);
    $[0] = context.initArgs.device;
    $[1] = requirement.user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const loginMethods = t1;
  const isOnlyBiometric = loginMethods.length === 1 && loginMethods[0] === "biometric";
  const shouldShowCallengeSelector = isUpdateLoginFlow(context.initArgs.variant) || loginMethods.length > 1 || isOnlyBiometric;
  const [screen, setScreen] = useState(Screen.Challenge);
  if (screen === Screen.LoginWithUnverifiedEmail) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setScreen(Screen.Challenge);
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== context || $[5] !== onDone || $[6] !== requirement) {
      t3 = <LoginWithUnverifiedEmail context={context} requirement={requirement} onDone={onDone} onPrev={t2} />;
      $[4] = context;
      $[5] = onDone;
      $[6] = requirement;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (shouldShowCallengeSelector) {
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setScreen(Screen.LoginWithUnverifiedEmail);
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== requirement) {
      t3 = <TryAnotherWay requirement={requirement} onClick={t2} />;
      $[9] = requirement;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== context || $[12] !== onReset || $[13] !== requirement) {
      t4 = <DifferentAccountOption onLoginWithDifferentAccount={onReset} context={context} requirement={requirement} />;
      $[11] = context;
      $[12] = onReset;
      $[13] = requirement;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== context || $[16] !== onDone || $[17] !== onPrev || $[18] !== requirement || $[19] !== t3 || $[20] !== t4) {
      t5 = <ChallengeSelectOrPasskey context={context} requirement={requirement} onDone={onDone} onPrev={onPrev}>{t3}{t4}</ChallengeSelectOrPasskey>;
      $[15] = context;
      $[16] = onDone;
      $[17] = onPrev;
      $[18] = requirement;
      $[19] = t3;
      $[20] = t4;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    return t5;
  }
  const challengeKind = loginMethods[0];
  const t2 = context.state.email?.value;
  const t3 = context.state.phoneNumber?.value;
  let t4;
  if ($[22] !== t2 || $[23] !== t3) {
    t4 = {
      email: t2,
      phoneNumber: t3
    };
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setScreen(Screen.LoginWithUnverifiedEmail);
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== requirement) {
    t6 = <TryAnotherWay requirement={requirement} onClick={t5} />;
    $[26] = requirement;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== context || $[29] !== onReset || $[30] !== requirement) {
    t7 = <DifferentAccountOption onLoginWithDifferentAccount={onReset} context={context} requirement={requirement} />;
    $[28] = context;
    $[29] = onReset;
    $[30] = requirement;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== children || $[33] !== t6 || $[34] !== t7) {
    t8 = <div className="flex flex-col gap-6">{t6}{t7}{children}</div>;
    $[32] = children;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== challengeKind || $[37] !== context || $[38] !== onDone || $[39] !== onPrev || $[40] !== requirement.user || $[41] !== t4 || $[42] !== t8) {
    t9 = <Challenge defaultValues={t4} context={context} onDone={onDone} onPrev={onPrev} onError={context.onError} user={requirement.user} challengeKind={challengeKind}>{t8}</Challenge>;
    $[36] = challengeKind;
    $[37] = context;
    $[38] = onDone;
    $[39] = onPrev;
    $[40] = requirement.user;
    $[41] = t4;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
};
export default LoginChallenge;